import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../../utils/supabase';
import { format } from 'date-fns';
import { Button, StatusBadge, MobileSearchField, LoadingState, Modal, ModalContent, ModalCloseButton } from '../common';
import PlayerAvatar from '../common/PlayerAvatar';
import { SessionLadderToggle } from '../session/SessionLadderToggle';
import { getGenderTextColor } from '../../utils/genderColors';
import { addPlayersToSession } from '../../services/sessionService';
import { bulkAddSessionSignups } from '../../services/sessionManagementService';
import { filterPlayersForHostSearch, getPlayerRosterTypeMeta } from '../../utils/clubPlayerLinkDedup';
import { playerMatchesSearchQuery } from '../../utils/playerUtils';
import { membershipFromSignupPlayer } from '../../utils/rosterPasteMatch';
import { isHostSelectSignup } from '../../utils/signupSelection';
import AddGuestModal from '../guests/AddGuestModal';
import PasteRosterModal from './PasteRosterModal';

const ADD_MATCH_LIMIT = 25;

const SORT_OPTIONS = [
  { value: 'signup_time:asc', label: 'Earliest' },
  { value: 'signup_time:desc', label: 'Latest' },
  { value: 'name:asc', label: 'Name A–Z' },
  { value: 'name:desc', label: 'Name Z–A' },
  { value: 'gender:asc', label: 'Gender F–M' },
  { value: 'gender:desc', label: 'Gender M–F' },
  { value: 'elo:desc', label: 'Elo high' },
  { value: 'elo:asc', label: 'Elo low' },
];

const SIGNUP_SECTIONS = [
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'waitlist', label: 'Waitlist' },
  { id: 'cancelled', label: 'Cancelled' },
];

const signupBucket = (player) => {
  if (!player?.is_signed_up) return null;
  if (player.signup_status === 'cancelled') return 'cancelled';
  if (player.signup_status === 'waitlist' || Number(player.waitlist_position) > 0) return 'waitlist';
  return 'confirmed';
};

const genderRank = (gender) => {
  const value = (gender || '').toLowerCase();
  if (value === 'female') return 0;
  if (value === 'male') return 1;
  return 2;
};

const playerSortName = (player) => `${player?.first_name || ''} ${player?.last_name || ''}`.trim();

const finiteRating = (value) => {
  if (value == null || value === '') return null;
  const rating = Number(value);
  return Number.isFinite(rating) ? Math.round(rating) : null;
};

const formatEloValue = (value) => (value == null ? '—' : value.toLocaleString());

const resolveSignupElo = (ratings, matchFormat) => {
  const mixed = finiteRating(ratings?.mixed_elo_rating);
  const levels = finiteRating(ratings?.levels_elo_rating);
  const singles = finiteRating(ratings?.singles_elo_rating);

  if (matchFormat === 'singles') {
    return {
      display: [{ label: 'Singles', value: singles }],
      sortValue: singles,
    };
  }

  return {
    display: [
      { label: 'Mixed', value: mixed },
      { label: 'Levels', value: levels },
    ],
    sortValue: mixed ?? levels,
  };
};

const compareWaitlistOrder = (a, b) => {
  const left = Number(a?.waitlist_position);
  const right = Number(b?.waitlist_position);
  const leftMissing = !Number.isFinite(left) || left <= 0;
  const rightMissing = !Number.isFinite(right) || right <= 0;
  if (leftMissing && rightMissing) return playerSortName(a).localeCompare(playerSortName(b), undefined, { sensitivity: 'base' });
  if (leftMissing) return 1;
  if (rightMissing) return -1;
  if (left !== right) return left - right;
  return playerSortName(a).localeCompare(playerSortName(b), undefined, { sensitivity: 'base' });
};

const compareSignups = (a, b, sortKey, sortDir, eloOf) => {
  const dir = sortDir === 'desc' ? -1 : 1;
  const byName = () => playerSortName(a).localeCompare(playerSortName(b), undefined, { sensitivity: 'base' });

  if (sortKey === 'name') return byName() * dir;

  if (sortKey === 'gender') {
    const diff = genderRank(a.gender) - genderRank(b.gender);
    return (diff || byName()) * dir;
  }

  if (sortKey === 'elo') {
    const left = eloOf(a)?.sortValue ?? null;
    const right = eloOf(b)?.sortValue ?? null;
    if (left == null && right == null) return byName();
    if (left == null) return 1;
    if (right == null) return -1;
    return ((left - right) || byName()) * dir;
  }

  const leftTime = a.signup_date ? new Date(a.signup_date).getTime() : null;
  const rightTime = b.signup_date ? new Date(b.signup_date).getTime() : null;
  if (leftTime == null && rightTime == null) return byName();
  if (leftTime == null) return 1;
  if (rightTime == null) return -1;
  return ((leftTime - rightTime) || byName()) * dir;
};

const ManageSignupsModal = ({ session, currentUser, onSessionCountsChanged }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [sortKey, setSortKey] = useState('signup_time');
  const [sortDir, setSortDir] = useState('asc');
  const [eloByPlayerId, setEloByPlayerId] = useState({});
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [showPasteRosterModal, setShowPasteRosterModal] = useState(false);
  const [addPlacementPlayer, setAddPlacementPlayer] = useState(null);
  const searchInputRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingOperations, setPendingOperations] = useState(new Set());
  const [localConfirmedCount, setLocalConfirmedCount] = useState(0);
  const [localWaitlistCount, setLocalWaitlistCount] = useState(0);

  // Get current season if session.season_id is null
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  
  useEffect(() => {
    if (session && !session.season_id) {
      fetchCurrentSeason();
    } else {
      setCurrentSeasonId(session?.season_id);
    }
  }, [session]);

  useEffect(() => {
    const seasonId = session?.season_id || currentSeasonId;
    if (!session?.club_id || !seasonId) return undefined;

    let cancelled = false;
    (async () => {
      const { data, error: eloError } = await supabase
        .from('player_club_elo_ratings')
        .select('player_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
        .eq('club_id', session.club_id)
        .eq('season_id', seasonId);

      if (cancelled) return;
      if (eloError) {
        console.error('[ManageSignups] Error fetching Elo:', eloError);
        return;
      }

      const next = {};
      (data || []).forEach((row) => {
        next[row.player_id] = row;
      });
      setEloByPlayerId(next);
    })();

    return () => {
      cancelled = true;
    };
  }, [session?.club_id, session?.id, session?.season_id, currentSeasonId]);

  const fetchCurrentSeason = async () => {
    if (!session?.club_id) return;
    
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('id')
        .eq('club_id', session.club_id)
        .eq('season_status', 'current')
        .single();
        
      if (error) {
        console.error('[ManageSignups] Error fetching current season:', error);
        // Try to get the most recent season if no 'current' season
        const { data: recentData, error: recentError } = await supabase
          .from('seasons')
          .select('id')
          .eq('club_id', session.club_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
          
        if (recentError) {
          console.error('[ManageSignups] Error fetching recent season:', recentError);
        } else {
          setCurrentSeasonId(recentData.id);
        }
      } else {
        setCurrentSeasonId(data.id);
      }
    } catch (err) {
      console.error('[ManageSignups] Error in fetchCurrentSeason:', err);
    }
  };

  useEffect(() => {
    fetchPlayers(true); // Initial load
  }, [session]);

  // Update local counts when players data changes
  useEffect(() => {
    const confirmed = players.filter(p => p.is_signed_up && p.signup_status === 'confirmed').length;
    const waitlist = players.filter(p => p.is_signed_up && p.signup_status === 'waitlist').length;
    setLocalConfirmedCount(confirmed);
    setLocalWaitlistCount(waitlist);
    
    // Notify parent component of count changes (if callback provided)
    if (onSessionCountsChanged) {
      onSessionCountsChanged(confirmed, waitlist);
    }
  }, [players, onSessionCountsChanged]);

  // Function to preserve scroll position during updates
  const preserveScrollPosition = (callback) => {
    const container = document.querySelector('.modal-content-scroll');
    const scrollTop = container ? container.scrollTop : 0;
    
    callback();
    
    // Restore scroll position after a brief delay to allow DOM update
    setTimeout(() => {
      if (container) {
        container.scrollTop = scrollTop;
      }
    }, 10);
  };

  const fetchPlayers = async (isInitialLoad = false) => {
    if (!session) return;
    
    // Only show loading spinner on initial load, not on refreshes
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    
    try {
      // Fetch all players in the club (including guests)
      const { data: clubPlayers, error: clubError } = await supabase
        .from('players_club')
        .select(`
          player_id,
          players (
            id,
            first_name,
            last_name,
            email,
            gender,
            is_guest,
            merged_at,
            guest_invited_email
          )
        `)
        .eq('club_id', session.club_id)
        .eq('is_active', true);

      if (clubError) throw clubError;

      // Fetch current signups for this session
      const { data: signups, error: signupError } = await supabase
        .from('session_signups')
        .select(`
          player_id,
          signup_date,
          waitlist_position,
          signup_status,
          added_by
        `)
        .eq('session_id', session.id);

      if (signupError) throw signupError;

      // Combine the data (exclude merged guests from signup search)
      const playersWithSignupStatus = clubPlayers
        .filter((clubPlayer) => {
          const player = clubPlayer.players;
          return !(player?.is_guest === true && player?.merged_at != null);
        })
        .map((clubPlayer) => {
        const signup = signups.find(s => s.player_id === clubPlayer.player_id);
        return {
          ...clubPlayer.players,
          signup_date: signup?.signup_date,
          waitlist_position: signup?.waitlist_position,
          signup_status: signup?.signup_status,
          added_by: signup?.added_by,
          is_signed_up: !!signup
        };
      });

      // Use preserveScrollPosition for smooth updates
      preserveScrollPosition(() => {
        setPlayers(playersWithSignupStatus);
      });
    } catch (err) {
      console.error('Error fetching players:', err);
      setError('Failed to fetch players');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const handleAddPlayer = async (playerId, signupStatus) => {
    if (signupStatus !== 'confirmed' && signupStatus !== 'waitlist') return;

    // Track this operation as pending
    setPendingOperations(prev => new Set([...prev, `add-${playerId}`]));
    
    try {
      // Find the player to add
      const playerToAdd = players.find(p => p.id === playerId);
      if (!playerToAdd) {
        setError('Player not found');
        return;
      }

      if (signupStatus === 'waitlist' && session.waitlist_enabled === false) {
        setError('Waitlist is off for this session.');
        return;
      }

      const placement = signupStatus === 'waitlist'
        ? { signupStatus: 'waitlist', waitlistPosition: localWaitlistCount + 1 }
        : { signupStatus: 'confirmed', waitlistPosition: null };

      // Optimistically update the UI immediately
      const optimisticPlayer = {
        ...playerToAdd,
        is_signed_up: true,
        signup_date: new Date().toISOString(),
        signup_status: placement.signupStatus,
        waitlist_position: placement.waitlistPosition,
      };

      // Update local state immediately for instant feedback
      setPlayers(prevPlayers => 
        prevPlayers.map(player => 
          player.id === playerId ? optimisticPlayer : player
        )
      );

      // Perform the actual database operation in the background
      // We'll handle this directly to avoid parent component refreshes
      const { error } = await supabase
        .from('session_signups')
        .insert({
          session_id: session.id,
          player_id: playerId,
          signup_status: placement.signupStatus,
          waitlist_position: placement.waitlistPosition,
          signup_date: new Date().toISOString(),
          added_by: currentUser.id
        });

      if (error) throw error;
      
      // If session is active (ongoing), also add player to session_players
      // This keeps both tables in sync when players are added during an active session
      if (session.session_status === 'ongoing') {
        try {
          // Check if player already exists in session_players to avoid duplicates
          const { data: existingPlayer, error: checkError } = await supabase
            .from('session_players')
            .select('id')
            .eq('session_id', session.id)
            .eq('player_id', playerId)
            .maybeSingle();
          
          if (checkError && checkError.code !== 'PGRST116') {
            // PGRST116 is "not found" which is fine, but other errors should be logged
            console.warn('Error checking existing session_players record:', checkError);
          }
          
          // Only add if player doesn't already exist in session_players
          if (!existingPlayer) {
            await addPlayersToSession(session.id, [playerId], session.club_id, 'signed_up');
            console.log('✅ Auto-added player to session_players for active session');
          }
        } catch (sessionPlayerError) {
          // Log error but don't fail the signup - session_signups was successful
          console.error('⚠️ Failed to auto-add player to session_players (signup still succeeded):', sessionPlayerError);
          // Optionally show a warning to the user
          setError('Player added to signups, but failed to add to active session. Please add them manually in Player Management.');
        }
      }
      
      setError(null);
    } catch (err) {
      console.error('Error adding player:', err);
      
      // Revert the optimistic update on error
      setPlayers(prevPlayers => 
        prevPlayers.map(player => 
          player.id === playerId ? { ...player, is_signed_up: false, signup_date: null, signup_status: null } : player
        )
      );
      
      setError(err.message || 'Failed to add player');
    } finally {
      // Remove from pending operations
      setPendingOperations(prev => {
        const newSet = new Set(prev);
        newSet.delete(`add-${playerId}`);
        return newSet;
      });
    }
  };

  const handleSetSignupStatus = async (playerId, nextStatus) => {
    const playerToUpdate = players.find((player) => player.id === playerId);
    if (!playerToUpdate) {
      setError('Player not found');
      return;
    }

    const currentStatus = signupBucket(playerToUpdate);
    if (!nextStatus || nextStatus === currentStatus) return;
    if (nextStatus === 'waitlist' && session.waitlist_enabled === false) {
      setError('Waitlist is off for this session.');
      return;
    }

    setPendingOperations((prev) => new Set([...prev, `status-${playerId}`]));

    const leavingWaitlist = currentStatus === 'waitlist' && nextStatus !== 'waitlist';
    const remainingWaitlist = leavingWaitlist
      ? players
          .filter((player) => player.id !== playerId && signupBucket(player) === 'waitlist')
          .sort(compareWaitlistOrder)
      : [];
    const waitlistPosition = nextStatus === 'waitlist'
      ? players.filter((player) => player.id !== playerId && signupBucket(player) === 'waitlist').length + 1
      : null;

    try {
      setPlayers((prevPlayers) =>
        prevPlayers.map((player) => {
          if (player.id === playerId) {
            return {
              ...player,
              is_signed_up: true,
              signup_status: nextStatus,
              waitlist_position: waitlistPosition,
            };
          }
          const queueIndex = remainingWaitlist.findIndex((row) => row.id === player.id);
          if (queueIndex >= 0) {
            return { ...player, waitlist_position: queueIndex + 1 };
          }
          return player;
        })
      );

      const { error } = await supabase
        .from('session_signups')
        .update({
          signup_status: nextStatus,
          waitlist_position: waitlistPosition,
        })
        .eq('session_id', session.id)
        .eq('player_id', playerId);

      if (error) throw error;

      if (remainingWaitlist.length > 0) {
        const renumberResults = await Promise.all(
          remainingWaitlist.map((player, index) => {
            const position = index + 1;
            if (Number(player.waitlist_position) === position) return Promise.resolve({ error: null });
            return supabase
              .from('session_signups')
              .update({ waitlist_position: position })
              .eq('session_id', session.id)
              .eq('player_id', player.id);
          })
        );
        const renumberError = renumberResults.find((result) => result?.error)?.error;
        if (renumberError) throw renumberError;
      }
      setError(null);
    } catch (err) {
      console.error('Error updating signup status:', err);
      setPlayers((prevPlayers) =>
        prevPlayers.map((player) => (player.id === playerId ? playerToUpdate : player))
      );
      setError(err.message || 'Failed to update signup status');
    } finally {
      setPendingOperations((prev) => {
        const next = new Set(prev);
        next.delete(`status-${playerId}`);
        return next;
      });
    }
  };

  const handleReorderWaitlist = async (playerId, nextPlace) => {
    const ordered = players
      .filter((player) => signupBucket(player) === 'waitlist')
      .sort(compareWaitlistOrder);
    const fromIndex = ordered.findIndex((player) => player.id === playerId);
    const toIndex = Math.max(0, Math.min(ordered.length - 1, Number(nextPlace) - 1));
    if (fromIndex < 0 || fromIndex === toIndex) return;

    const nextOrder = [...ordered];
    const [moved] = nextOrder.splice(fromIndex, 1);
    nextOrder.splice(toIndex, 0, moved);
    const positionById = new Map(nextOrder.map((player, index) => [player.id, index + 1]));
    const previousPlayers = players;

    setPendingOperations((prev) => new Set([...prev, 'reorder-waitlist']));
    setPlayers((prevPlayers) =>
      prevPlayers.map((player) =>
        positionById.has(player.id)
          ? { ...player, signup_status: 'waitlist', waitlist_position: positionById.get(player.id) }
          : player
      )
    );

    try {
      const results = await Promise.all(
        nextOrder.map((player, index) => {
          const position = index + 1;
          if (Number(player.waitlist_position) === position) return Promise.resolve({ error: null });
          return supabase
            .from('session_signups')
            .update({ signup_status: 'waitlist', waitlist_position: position })
            .eq('session_id', session.id)
            .eq('player_id', player.id);
        })
      );
      const failed = results.find((result) => result?.error);
      if (failed?.error) throw failed.error;
      setError(null);
    } catch (err) {
      console.error('Error reordering waitlist:', err);
      setPlayers(previousPlayers);
      setError(err.message || 'Failed to reorder the waitlist');
    } finally {
      setPendingOperations((prev) => {
        const next = new Set(prev);
        next.delete('reorder-waitlist');
        return next;
      });
    }
  };

  const handleGuestAdded = async (guest) => {
    try {
      // Optimistically add the guest to the local player list
      const optimisticGuest = {
        ...guest,
        is_signed_up: true,
        signup_date: new Date().toISOString(),
        signup_status: isHostSelectSignup(session) ? 'waitlist' : 'confirmed',
        waitlist_position: isHostSelectSignup(session) ? localWaitlistCount + 1 : null,
      };

      // Add the guest to the local state immediately
      setPlayers(prevPlayers => [...prevPlayers, optimisticGuest]);

      // Note: The actual database operation is handled by the AddGuestModal
      // We just need to update our local state here
    } catch (error) {
      setError('Failed to add guest to local state');
    }
  };

  const signedUpPlayerIds = useMemo(
    () =>
      new Set(
        players
          .filter((player) => player.is_signed_up && player.signup_status !== 'cancelled')
          .map((player) => player.id)
      ),
    [players]
  );

  const handlePasteRosterAccept = async (writableRows) => {
    const assignments = (writableRows || []).map((row) => ({
      playerId: row.selectedPlayerId,
      signupStatus: row.placement,
      waitlistPosition: row.waitlistPosition,
    }));
    const result = await bulkAddSessionSignups({
      sessionId: session.id,
      addedBy: currentUser?.id,
      assignments,
    });
    await fetchPlayers(false);
    return {
      addedCount: result.added,
      confirmedCount: result.confirmed,
      waitlistCount: result.waitlist,
      alreadyCount: result.already,
    };
  };

  const rosterPlayers = useMemo(
    () => filterPlayersForHostSearch(players, { signedUpPlayerIds }),
    [players, signedUpPlayerIds]
  );

  const signupPlayers = useMemo(
    () => rosterPlayers.filter((player) => signupBucket(player) != null),
    [rosterPlayers]
  );

  const eloFor = useCallback(
    (player) => resolveSignupElo(eloByPlayerId[player?.id], session?.competitive_match_format),
    [eloByPlayerId, session?.competitive_match_format]
  );

  const filteredSignups = useMemo(() => {
    const statusFiltered = signupPlayers.filter((player) => {
      const bucket = signupBucket(player);
      if (filterStatus === 'confirmed') return bucket === 'confirmed';
      if (filterStatus === 'waitlist') return bucket === 'waitlist';
      if (filterStatus === 'cancelled') return bucket === 'cancelled';
      return true;
    });
    const searched = statusFiltered.filter((player) => playerMatchesSearchQuery(player, searchTerm));
    return [...searched].sort((a, b) => compareSignups(a, b, sortKey, sortDir, eloFor));
  }, [signupPlayers, filterStatus, searchTerm, sortKey, sortDir, eloFor]);

  const signupSections = useMemo(() => {
    const groups = { confirmed: [], waitlist: [], cancelled: [] };
    filteredSignups.forEach((player) => {
      const bucket = signupBucket(player);
      if (groups[bucket]) groups[bucket].push(player);
    });
    groups.waitlist.sort(compareWaitlistOrder);
    return SIGNUP_SECTIONS.map((section) => ({
      ...section,
      players: groups[section.id],
    }));
  }, [filteredSignups]);

  const orderedWaitlist = useMemo(
    () => signupPlayers.filter((player) => signupBucket(player) === 'waitlist').sort(compareWaitlistOrder),
    [signupPlayers]
  );

  const addMatches = useMemo(() => {
    const query = addQuery.trim();
    if (!query) return { rows: [], total: 0 };
    const pool = rosterPlayers.filter((player) => signupBucket(player) == null);
    const matches = pool
      .filter((player) => playerMatchesSearchQuery(player, query))
      .sort((a, b) => compareSignups(a, b, 'name', 'asc', () => null));
    return { rows: matches.slice(0, ADD_MATCH_LIMIT), total: matches.length };
  }, [rosterPlayers, addQuery]);

  const isSessionActive = session.session_status === 'ongoing';
  const sessionIsFull =
    !isHostSelectSignup(session) &&
    localConfirmedCount >= session.max_players &&
    !session.waitlist_enabled;
  const sortValue = `${sortKey}:${sortDir}`;

  const handleSortChange = (event) => {
    const [nextKey, nextDir] = event.target.value.split(':');
    setSortKey(nextKey);
    setSortDir(nextDir === 'desc' ? 'desc' : 'asc');
  };

  const toggleAddPanel = () => {
    setShowAddPanel((open) => {
      if (open) setAddQuery('');
      return !open;
    });
  };

  const formatSignupTime = (dateString) => format(new Date(dateString), 'MMM d, h:mm a');

  const renderElo = (player) => {
    const elo = eloFor(player);
    const parts = elo?.display || [];
    if (!parts.length || parts.every((part) => part.value == null)) {
      return <span className="text-text-muted">Elo —</span>;
    }
    return (
      <span className="tabular-nums text-text-secondary">
        {parts.map((part, index) => (
          <React.Fragment key={part.label}>
            {index > 0 ? (
              <span className="text-border-medium" aria-hidden>
                {' · '}
              </span>
            ) : null}
            <span title={`${part.label} Elo`}>
              {part.label} {formatEloValue(part.value)}
            </span>
          </React.Fragment>
        ))}
      </span>
    );
  };

  const renderSignupRow = (player, index, queue = null) => {
    const guestTypeMeta = player.is_guest ? getPlayerRosterTypeMeta(player) : null;
    const bucket = signupBucket(player);
    const sessionLockedTitle = isSessionActive
      ? 'Session has started. Please manage players via Player Management.'
      : undefined;
    const queueLocked = isSessionActive || pendingOperations.has('reorder-waitlist') || pendingOperations.has(`status-${player.id}`);
    return (
      <li key={player.id} className="flex items-center gap-3 px-4 py-3 md:gap-4 md:px-5">
        {queue ? (
          <div className="w-16 shrink-0 md:w-[4.5rem]">
            <label className="sr-only" htmlFor={`waitlist-place-${player.id}`}>
              Queue place for {player.first_name} {player.last_name}
            </label>
            <select
              id={`waitlist-place-${player.id}`}
              value={String(queue.place)}
              disabled={queueLocked || queue.size < 2}
              onChange={(event) => handleReorderWaitlist(player.id, Number(event.target.value))}
              title={sessionLockedTitle || '1 is first in line'}
              aria-label={`Queue place for ${player.first_name} ${player.last_name}`}
              className="form-input h-11 w-full rounded-md border border-border-medium bg-field px-2 text-center text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {Array.from({ length: queue.size }, (_, placeIndex) => (
                <option key={placeIndex + 1} value={placeIndex + 1}>
                  {placeIndex + 1}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <span className="w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-text-muted">
            {index + 1}
          </span>
        )}
        <PlayerAvatar player={player} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={`truncate text-sm font-medium ${getGenderTextColor(player.gender)}`}>
              {player.first_name} {player.last_name}
            </span>
            {guestTypeMeta ? (
              <StatusBadge variant={guestTypeMeta.variant} size="small" className="shrink-0">
                {guestTypeMeta.label}
              </StatusBadge>
            ) : null}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs md:hidden">
            {renderElo(player)}
            {player.signup_date ? (
              <span className="text-text-muted">{formatSignupTime(player.signup_date)}</span>
            ) : null}
          </div>
        </div>
        <div className="hidden min-w-[11rem] shrink-0 text-sm md:block">
          {renderElo(player)}
        </div>
        <div className="hidden w-[9.5rem] shrink-0 text-sm text-text-muted md:block">
          {player.signup_date ? formatSignupTime(player.signup_date) : '—'}
        </div>
        <div className="w-[8.5rem] shrink-0 md:w-44">
          <label className="sr-only" htmlFor={`signup-status-${player.id}`}>
            Status for {player.first_name} {player.last_name}
          </label>
          <select
            id={`signup-status-${player.id}`}
            value={bucket || 'confirmed'}
            disabled={isSessionActive || pendingOperations.has(`status-${player.id}`)}
            onChange={(event) => handleSetSignupStatus(player.id, event.target.value)}
            title={sessionLockedTitle || 'Change signup status'}
            aria-label={`Status for ${player.first_name} ${player.last_name}`}
            className="form-input h-11 w-full rounded-md border border-border-medium bg-field px-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="confirmed">Confirmed</option>
            <option value="waitlist" disabled={session.waitlist_enabled === false && bucket !== 'waitlist'}>
              Waitlist
            </option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </li>
    );
  };

  return (
    <div>
          {error ? (
            <div className="mb-3 rounded-lg border border-danger/30 bg-danger-light p-3">
              <p className="text-sm text-danger-dark">{error}</p>
            </div>
          ) : null}

          <div className="max-w-full overflow-x-auto">
            <SessionLadderToggle
              variant="light"
              ariaLabel="Filter signups"
              value={filterStatus}
              onChange={setFilterStatus}
              tabs={[
                { id: 'all', label: 'All' },
                { id: 'confirmed', label: 'Confirmed' },
                { id: 'waitlist', label: 'Waitlist' },
                { id: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>

          <div className="mt-4 flex min-w-0 items-center gap-2 md:gap-3">
            <div className="min-w-0 flex-1">
              <MobileSearchField
                inputRef={searchInputRef}
                placeholder="Search signups"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search signups by name or email"
                className="form-input w-full rounded-md border border-border-medium bg-field px-3 py-2 text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="w-[9rem] shrink-0 md:w-44">
              <label className="sr-only" htmlFor="signup-sort">
                Sort signups
              </label>
              <select
                id="signup-sort"
                value={sortValue}
                onChange={handleSortChange}
                className="form-input h-11 w-full rounded-md border border-border-medium bg-field px-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                aria-label="Sort signups"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3 overflow-hidden rounded-xl border border-border-medium bg-surface shadow-brand-sm">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-60"
              onClick={toggleAddPanel}
              aria-expanded={showAddPanel}
              aria-controls="signup-add-panel"
              disabled={isSessionActive}
              title={
                isSessionActive
                  ? 'Session has started. Please add players via Player Management.'
                  : showAddPanel
                    ? 'Hide add a player'
                    : 'Search the club, paste a list, or add a new guest'
              }
            >
              <span className="min-w-0 text-sm font-semibold text-text-primary">Add a player</span>
              <svg
                className={`h-5 w-5 shrink-0 text-text-muted transition-transform duration-200 ${showAddPanel ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showAddPanel ? (
            <div id="signup-add-panel" className="border-t border-border-medium px-4 pb-4 pt-3 md:px-5">
              <div className="md:flex md:items-start md:gap-3">
                <div className="min-w-0 flex-1">
                  <MobileSearchField
                    placeholder="Search the club by name"
                    value={addQuery}
                    onChange={(e) => setAddQuery(e.target.value)}
                    aria-label="Search club players to add"
                    disabled={isSessionActive}
                    className="form-input w-full rounded-md border border-border-medium bg-field px-3 py-2 text-text-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2 md:mt-0 md:shrink-0">
                {!isSessionActive ? (
                  <Button
                    onClick={() => setShowPasteRosterModal(true)}
                    variant="outline"
                    size="small"
                    title="Paste a list of names from a spreadsheet"
                  >
                    Paste list
                  </Button>
                ) : null}
                <Button
                  onClick={() => setShowAddGuestModal(true)}
                  variant="outline"
                  size="small"
                  disabled={isSessionActive}
                  title={
                    isSessionActive
                      ? 'Session has started. Please add players via Player Management.'
                      : 'Create a new guest and add them to this session'
                  }
                >
                  Add New Guest
                </Button>
                </div>
              </div>
              {addQuery.trim() ? (
                addMatches.total === 0 ? (
                  <p className="mt-3 text-sm text-text-secondary">No club players match. Try another spelling, or add them as a guest.</p>
                ) : (
                  <div className="mt-3 overflow-hidden rounded-lg border border-border-medium">
                    {addMatches.total > addMatches.rows.length ? (
                      <p className="border-b border-border-medium px-3 py-2 text-xs text-text-muted">
                        Showing {addMatches.rows.length} of {addMatches.total}. Keep typing to narrow the list.
                      </p>
                    ) : null}
                    <ul className="divide-y divide-border-medium">
                      {addMatches.rows.map((player) => {
                        const guestTypeMeta = player.is_guest ? getPlayerRosterTypeMeta(player) : null;
                        const adding = pendingOperations.has(`add-${player.id}`);
                        return (
                          <li key={player.id} className="flex items-center gap-3 px-3 py-2 md:gap-4 md:px-4">
                            <PlayerAvatar player={player} size="md" />
                            <div className="min-w-0 flex-1">
                              <div className="flex min-w-0 items-center gap-2">
                                <span className={`truncate text-sm font-medium ${getGenderTextColor(player.gender)}`}>
                                  {player.first_name} {player.last_name}
                                </span>
                                {guestTypeMeta ? (
                                  <StatusBadge variant={guestTypeMeta.variant} size="small" className="shrink-0">
                                    {guestTypeMeta.label}
                                  </StatusBadge>
                                ) : null}
                              </div>
                              <p className="truncate text-xs text-text-muted">
                                {player.email || (player.is_guest ? 'No email' : 'Club member')}
                                <span className="md:hidden">
                                  {' · '}
                                  {renderElo(player)}
                                </span>
                              </p>
                            </div>
                            <div className="hidden min-w-[11rem] shrink-0 text-sm md:block">
                              {renderElo(player)}
                            </div>
                            <Button
                              onClick={() => setAddPlacementPlayer(player)}
                              disabled={isSessionActive || sessionIsFull || adding}
                              variant={sessionIsFull ? 'secondary' : 'primary'}
                              size="small"
                              className="shrink-0 px-3"
                              title={
                                isSessionActive
                                  ? 'Session has started. Please add players via Player Management.'
                                  : sessionIsFull
                                    ? 'Session is full and the waitlist is off.'
                                    : 'Choose waitlist or confirmed'
                              }
                            >
                              {adding ? 'Adding…' : 'Add'}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )
              ) : null}
            </div>
            ) : null}
          </div>

          {loading ? (
            <div className="mt-3 flex flex-1 items-center justify-center">
              <LoadingState message="Loading signups…" size="small" />
            </div>
          ) : (
            <div className="relative mt-4 overflow-hidden rounded-xl border border-border-medium bg-surface shadow-brand-sm">
              {isRefreshing ? (
                <div className="absolute right-2 top-2 z-10">
                  <div className="flex items-center gap-1 rounded-full bg-info-light px-2 py-1 text-xs text-info-dark">
                    <div className="h-3 w-3 animate-spin rounded-full border border-info border-t-transparent" />
                    <span>Updating…</span>
                  </div>
                </div>
              ) : null}

              {filteredSignups.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-sm font-semibold text-text-primary">
                    {searchTerm || filterStatus !== 'all' ? 'No signups match' : 'No signups yet'}
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try another name or status.'
                      : 'Open Add a player above to search the club or paste a list.'}
                  </p>
                </div>
              ) : filterStatus === 'all' ? (
                <div>
                  {signupSections.map((section, sectionIndex) => {
                    if (searchTerm && section.players.length === 0) return null;
                    return (
                      <section
                        key={section.id}
                        aria-label={section.label}
                        className={sectionIndex > 0 ? 'border-t border-border-medium' : undefined}
                      >
                        <h2 className="flex items-center justify-between bg-surface-subtle px-4 py-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                          <span>{section.label}</span>
                          <span className="tabular-nums">{section.players.length}</span>
                        </h2>
                        {section.players.length === 0 ? (
                          <p className="px-4 py-3 text-sm text-text-muted">None</p>
                        ) : (
                          <ol className="m-0 list-none divide-y divide-border-medium border-t border-border-medium p-0">
                            {section.players.map((player, index) => renderSignupRow(
                              player,
                              index,
                              section.id === 'waitlist'
                                ? {
                                    place: Math.max(1, orderedWaitlist.findIndex((row) => row.id === player.id) + 1),
                                    size: orderedWaitlist.length,
                                  }
                                : null
                            ))}
                          </ol>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <>
                <ol className="m-0 list-none divide-y divide-border-medium p-0">
                  {(filterStatus === 'waitlist'
                    ? [...filteredSignups].sort(compareWaitlistOrder)
                    : filteredSignups
                  ).map((player, index) => renderSignupRow(
                    player,
                    index,
                    filterStatus === 'waitlist'
                      ? {
                          place: Math.max(1, orderedWaitlist.findIndex((row) => row.id === player.id) + 1),
                          size: orderedWaitlist.length,
                        }
                      : null
                  ))}
                </ol>
                </>
              )}
            </div>
          )}

          <Modal
            isOpen={!!addPlacementPlayer}
            onClose={() => setAddPlacementPlayer(null)}
          >
            <ModalContent size="small" constrainHeight={false}>
              <ModalCloseButton onClose={() => setAddPlacementPlayer(null)} />
              <h3 className="pr-10 text-lg font-medium text-text-primary">
                Add {addPlacementPlayer?.first_name} {addPlacementPlayer?.last_name}
              </h3>
              <p className="mt-2 text-sm text-text-secondary">
                Add them to the waitlist or confirm them for this session.
              </p>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  size="small"
                  className="w-full sm:w-auto"
                  disabled={session.waitlist_enabled === false}
                  title={session.waitlist_enabled === false ? 'Waitlist is off for this session.' : undefined}
                  onClick={() => {
                    const playerId = addPlacementPlayer?.id;
                    setAddPlacementPlayer(null);
                    if (playerId) handleAddPlayer(playerId, 'waitlist');
                  }}
                >
                  Add to waitlist
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    const playerId = addPlacementPlayer?.id;
                    setAddPlacementPlayer(null);
                    if (playerId) handleAddPlayer(playerId, 'confirmed');
                  }}
                >
                  Add to confirmed
                </Button>
              </div>
            </ModalContent>
          </Modal>
          <AddGuestModal
            isOpen={showAddGuestModal}
            onClose={() => setShowAddGuestModal(false)}
            onGuestAdded={handleGuestAdded}
            sessionId={session.id}
            clubId={session.club_id}
            currentUser={currentUser}
            seasonId={currentSeasonId}
          />
          <PasteRosterModal
            isOpen={showPasteRosterModal}
            onClose={() => setShowPasteRosterModal(false)}
            mode="signup"
            players={players}
            getMembership={membershipFromSignupPlayer}
            signedUpPlayerIds={signedUpPlayerIds}
            capacity={{
              confirmedCount: localConfirmedCount,
              maxPlayers: session.max_players || 16,
              waitlistEnabled: session.waitlist_enabled !== false || isHostSelectSignup(session),
              waitlistCount: localWaitlistCount,
              signupSelection: session.signup_selection,
            }}
            clubId={session.club_id}
            seasonId={currentSeasonId}
            currentUser={currentUser}
            onAccept={handlePasteRosterAccept}
          />
    </div>
  );
};

export default ManageSignupsModal;
